import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, ListGroup, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchCustomerById } from '../../api/customer';

const CustomerDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await fetchCustomerById(id);
                setCustomer(res.data.data);
            } catch (err) {
                console.error('Failed to fetch customers:', err);
                if (err.status === 401) {
                    alert('Session expired. Please log in again.');
                    localStorage.removeItem('ums_user');
                    navigate('/login');
                } else {
                    setError('Failed to load customer details.');
                }
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [id]);

    const handleVerify = async () => {
        try {
            // TODO: Add actual verification API call here (e.g., `await verifyCustomer(id)`)
            // Redirect to application form
            navigate(`/crm/application/${id}`);
        } catch (err) {
            console.error('Verification failed:', err);
            alert('Verification failed.');
        }
    };

    const handleScheduleInspection = async () => {
        try {
            // TODO: Add actual verification API call here (e.g., `await verifyCustomer(id)`)
            // Redirect to application form
            navigate(`/field/schedule-inspection/${id}`);
        } catch (err) {
            console.error('Verification failed:', err);
            alert('Verification failed.');
        }
    };

    if (loading) return <Spinner animation="border" />;
    if (error) return <Alert variant="danger">{error}</Alert>;
    if (!customer) return <Alert variant="warning">No customer found.</Alert>;

    return (
        <div className="container">
            {/* Basic Info Card */}
            <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <strong>Customer Information</strong>
                    <Button size="sm" variant="success" onClick={handleVerify}>
                        ✅ Modify
                    </Button>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <p><strong>Account Number:</strong> {customer.accountNumber}</p>
                            <p><strong>Customer Type:</strong> {customer.customerType}</p>
                            <p><strong>Connected Load:</strong> {customer.connectedLoad}</p>
                            <p><strong>Rate Class:</strong> {customer.rateClass}</p>
                        </Col>
                        <Col md={6}>
                            <p><strong>Customer:</strong> {customer.firstName} {customer.lastName}</p>
                            <p><strong>Birthdate:</strong> {customer.birthdate}</p>
                            <p><strong>Nationality:</strong> {customer.nationality}</p>
                            <p><strong>Sex:</strong> {customer.sex}</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Tabs Card */}
            <Card className="shadow-sm">
                <Card.Header>
                    <strong>Customer Details</strong>
                </Card.Header>
                <Card.Body>
                    <Tabs defaultActiveKey="address" id="customer-tabs" className="mb-3">
                        <Tab eventKey="address" title="Address Info">
                            <ListGroup variant="flush">
                                <ListGroup.Item><strong>Landmark:</strong> {customer.landmark}</ListGroup.Item>
                                <ListGroup.Item><strong>House No:</strong> {customer.houseNumber}</ListGroup.Item>
                                <ListGroup.Item><strong>Floor:</strong> {customer.floor}</ListGroup.Item>
                                <ListGroup.Item><strong>Street:</strong> {customer.street}</ListGroup.Item>
                                <ListGroup.Item><strong>Subdivision:</strong> {customer.subdivision}</ListGroup.Item>
                                <ListGroup.Item><strong>Barangay:</strong> {customer.barangay}</ListGroup.Item>
                                <ListGroup.Item><strong>District:</strong> {customer.district}</ListGroup.Item>
                            </ListGroup>
                        </Tab>

                        <Tab eventKey="contact" title="Contact Info">
                            <ListGroup variant="flush">
                                <ListGroup.Item><strong>Mobile 1:</strong> {customer.mobile1}</ListGroup.Item>
                                <ListGroup.Item><strong>Mobile 2:</strong> {customer.mobile2}</ListGroup.Item>
                                <ListGroup.Item><strong>Tel 1:</strong> {customer.tel1}</ListGroup.Item>
                                <ListGroup.Item><strong>Tel 2:</strong> {customer.tel2}</ListGroup.Item>
                                <ListGroup.Item><strong>Email:</strong> {customer.contactEmail}</ListGroup.Item>
                                <ListGroup.Item>
                                    <strong>Contact Person:</strong> {customer.contactFirstName} {customer.contactMiddleName} {customer.contactLastName} {customer.contactSuffix}
                                </ListGroup.Item>
                                <ListGroup.Item><strong>Relationship:</strong> {customer.contactRelationship}</ListGroup.Item>
                            </ListGroup>
                        </Tab>

                        <Tab eventKey="requirements" title="Requirements">
                            <ListGroup variant="flush">
                                <ListGroup.Item><strong>ID Type:</strong> {customer.idTypeText}</ListGroup.Item>
                                <ListGroup.Item><strong>ID Category:</strong> {customer.idCategory}</ListGroup.Item>
                                <ListGroup.Item><strong>ID Number:</strong> {customer.idNumber}</ListGroup.Item>
                                <ListGroup.Item><strong>Senior Citizen:</strong> {customer.isSenior ? 'Yes' : 'No'}</ListGroup.Item>
                                <ListGroup.Item><strong>SC Start Date:</strong> {customer.scDateStart}</ListGroup.Item>
                                <ListGroup.Item><strong>OSCA ID:</strong> {customer.oscaId}</ListGroup.Item>
                            </ListGroup>
                        </Tab>

                        <Tab eventKey="billing" title="Bill Info">
                            <ListGroup variant="flush">
                                <ListGroup.Item><strong>Billing Address:</strong> {customer.billStreet}, {customer.billBarangay}, {customer.billDistrict}</ListGroup.Item>
                                <ListGroup.Item><strong>Landmark:</strong> {customer.billLandmark}</ListGroup.Item>
                                <ListGroup.Item><strong>Subdivision:</strong> {customer.billSubdivision}</ListGroup.Item>
                                <ListGroup.Item><strong>Building:</strong> {customer.billBldg}</ListGroup.Item>
                                <ListGroup.Item><strong>Unit:</strong> {customer.billUnit}</ListGroup.Item>
                                <ListGroup.Item><strong>Delivery Method:</strong> {customer.billDelivery}</ListGroup.Item>
                            </ListGroup>
                        </Tab>
                    </Tabs>
                </Card.Body>
            </Card>
        </div>
    );
};

export default CustomerDetail;
